import { Component, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import Entry from './pages/Entry';
import Nav from './components/Nav';
import Footer from './components/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Industries from './pages/Industries';
import IndustryDetail from './pages/IndustryDetail';
import HowItWorks from './pages/HowItWorks';
import Resources from './pages/Resources';
import Warranty from './pages/Warranty';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import CookiePreferences from './pages/CookiePreferences';
import AccessibilityStatement from './pages/AccessibilityStatement';
import About from './pages/About';
import Quote from './pages/Quote';
import Compatibility from './pages/Compatibility';
import Manufacturers from './pages/Manufacturers';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import AnalyticsTracker from './analytics/AnalyticsTracker';
import CookieBanner from './components/CookieBanner';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(error) {
    return { err: error };
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ minHeight: '100vh', background: '#050505', color: '#B8B8B8', padding: '96px 24px', fontFamily: 'sans-serif' }}>
          <h1 style={{ color: '#fff', textTransform: 'uppercase', fontSize: 28 }}>Something went wrong</h1>
          <p style={{ marginTop: 12 }}>
            Reload the page — if the problem persists, contact{' '}
            <a style={{ color: '#fff' }} href="mailto:commercial@rangesafe.com">commercial@rangesafe.com</a>.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function SiteChrome() {
  const { pathname } = useLocation();
  const isEntry = pathname === '/';
  return (
    <div className="grain vignette flex min-h-screen flex-col">
      {!isEntry && <Nav />}
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Entry />} />
          <Route path="/commercial" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<IndustryDetail />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/warranty" element={<Warranty />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/cookie-preferences" element={<CookiePreferences />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/about" element={<About />} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/compatibility" element={<Compatibility />} />
          <Route path="/oem" element={<Navigate to="/manufacturers" replace />} />
          <Route path="/manufacturers" element={<Manufacturers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      {!isEntry && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <AnalyticsTracker />
        <CookieBanner />
        <SiteChrome />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
